/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxResources as IBuyboxExtentionResources } from '../definition-extensions/buybox.ext.props.autogenerated';
import * as React from 'react';

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure,
        ...otherProps
    } = props;

    React.useEffect(()=>{
        _loadTamaraRenderScript();
        _loadTabbyRenderScript(otherProps.data?.productPrice?.result?.CustomerContextualPrice, otherProps.context.request?.locale);
    }, [price]);

    const [findInStoreOpened, setFindInStoreOpened] = React.useState(false);

    //Need this to refresh pick up stores after dimension update
    React.useEffect(()=>{
        try {
            const missingDimensions =
                otherProps.data.product.result?.Dimensions &&
                otherProps.data.product.result?.Dimensions.filter((dimension: any) => !(dimension.DimensionValue && dimension.DimensionValue.Value));

            if ( !missingDimensions || missingDimensions.length === 0 ) {
                if (findInStoreOpened) {
                    otherProps.data.storeSelectorStateManager.result?.closeDialog();
                    setFindInStoreOpened(false);
                }
                findInStore && findInStore.openFindInStoreDialog();
                setFindInStoreOpened(true);
            }
        }
        catch (ex) {
            console.log(ex);
        }
    }, [props, otherProps.data.product.result?.Dimensions]);
    

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>
                {props.mediaGallery}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {_renderTextBlock(props.slots.textBlocks)}
                {unitOfMeasure}
                {description}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity, callbacks, props, state, resources, quantityLimitsMessages, max, telemetryContent)}
                {inventoryLabel}
                {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                {_renderTamaraBlock(otherProps.data.productPrice.result?.CustomerContextualPrice, otherProps.context.request.locale)}
                {_renderTabbyBlock()}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots?.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderTamaraBlock = (price?: number, locale?: string): JSX.Element | undefined => {
    if (price && price > 100) {
        let lang = locale?.startsWith("ar") ? 'ar' : 'en';
        return (
            <div className="tamara-product-widget" data-lang={lang} data-price={price} data-currency="SAR" data-country-code="SA" data-color-type="default" data-show-border="false" data-payment-type="installment" data-number-of-installments="3" data-disable-installment="false" data-disable-paylater="true" > 
            </div>
        );
    }
    return undefined;
};

const _renderTabbyBlock = (): JSX.Element | undefined => {
    return (
        <div id="tabby-product-widget"></div>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return (
        <>
            {textBlocks[0]}
        </>
    );
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return (
        <>
            {socialShare[0]}
        </>
    );
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return (
        <Node {...ContainerProps}>
            {dropdowns.map(_renderConfigureDropdown)}
        </Node>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, /*heading, description,*/ errors, /*button,*/ modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {/*heading*/}
            {productPickupOptionList}
            {/*description*/}
            {errors}
            {/*button*/}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    telemetryContent?: ITelemetryContent): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const {
        resources
    } = props;

    const
        {
            quantity
        } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};


const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
            </Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

const _loadTabbyRenderScript = (price?: number, locale?: string): void => {
    const tabbyLoadScriptId = 'tabby-load-script';
    let lang = locale?.startsWith("ar") ? 'ar' : 'en';
    const tabbyLoadScript = document.getElementById(tabbyLoadScriptId);
    if (!tabbyLoadScript) {
        loadScript('https://checkout.tabby.ai/tabby-promo.js', tabbyLoadScriptId);
        var intervalId = setInterval(() => {
            //@ts-ignore
            const tabbyPromo = window && window.TabbyPromo;
            if (tabbyPromo) {
                new tabbyPromo({
                    selector: '#tabby-product-widget',
                    lang: lang, // 'ar'
                    currency: 'SAR', // 'SAR, AED, KWD, BHD'
                    price: price,
                    installmentsCount: 4,
                    source: 'product'
                  });
                clearInterval(intervalId)
            }
        }, 500)
    }
};

const _loadTamaraRenderScript = (): void => {
    const tamaraLoadScriptId = 'tamara-load-script';
    const tamaraLoadScript = document.getElementById(tamaraLoadScriptId);
    if (!tamaraLoadScript) {
        loadScript('https://cdn.tamara.co/widget/product-widget.min.js', tamaraLoadScriptId);
        var intervalId = setInterval(() => {
            //@ts-ignore
            const tamaraProductWidget = window && window.TamaraProductWidget;
            if (tamaraProductWidget) {
                tamaraProductWidget.init({ lang: 'en' })
                tamaraProductWidget.render()
                clearInterval(intervalId)
            }
        }, 500)
    }
};

const loadScript = (src: any, id: string): void => {
    if(!document.getElementById(id)){
        const tag = document.createElement('script');
        tag.async = false;
        tag.src = src;
        tag.id = id;
        const body = document.getElementsByTagName('body')[0];
        body.appendChild(tag);
    }
} 

export default BuyboxView;

